import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * One titled block of a slot sheet. Sections are separated by full-bleed rules,
 * so the sheet reads as a stack rather than a set of nested cards.
 *
 * @param props.title - the section heading
 * @param props.icon - optional icon rendered before the heading
 * @param props.trailing - optional node aligned to the right of the heading
 * @param props.className - extra classes merged on the section
 * @param props.children - the section content
 * @returns the section element
 */
export const SlotSheetSection = ({
  title,
  icon: Icon,
  trailing,
  className,
  children,
}: {
  title: ReactNode;
  icon?: LucideIcon;
  trailing?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section className={cn('border-b px-6 py-5 last:border-b-0', className)}>
    <header className="mb-3 flex items-center gap-2">
      <h3 className="flex items-center gap-2 font-semibold text-sm">
        {Icon && (
          <Icon
            className="size-4 text-muted-foreground"
            aria-hidden="true"
          />
        )}
        {title}
      </h3>
      {trailing && <div className="ml-auto">{trailing}</div>}
    </header>
    {children}
  </section>
);

/**
 * Two-column definition list used inside sections: a muted label column of
 * fixed width, then the value.
 *
 * @param props.children - alternating `SlotSheetRow` entries
 * @returns the definition list element
 */
export const SlotSheetDefinitionList = ({
  children,
}: {
  children: ReactNode;
}) => (
  <dl className="grid grid-cols-[minmax(0,11rem)_1fr] items-center gap-x-4 gap-y-2.5 text-sm">
    {children}
  </dl>
);

/**
 * One label/value pair of a `SlotSheetDefinitionList`.
 *
 * @param props.label - the row label
 * @param props.alignTop - align the label with the top of a multi-line value
 * @param props.children - the row value
 * @returns the label and value elements
 */
export const SlotSheetRow = ({
  label,
  alignTop,
  children,
}: {
  label: ReactNode;
  alignTop?: boolean;
  children: ReactNode;
}) => (
  <>
    <dt className={cn('text-muted-foreground', alignTop && 'self-start pt-1')}>
      {label}
    </dt>
    <dd className={cn('min-w-0', alignTop && 'self-start')}>{children}</dd>
  </>
);
